import React, { ReactNode, useState } from 'react';
import { ResponsiveContainer, Legend, Tooltip, PieChart, Pie, Cell } from 'recharts';
import { Widget } from './Widget';
import { DataResult, WidgetSettingsDto } from '@thingslog/repositories';
import { DataResultParser } from './parsing/DataResultParser';
import { DataResultParsed, DataResultParseStream } from './parsing/DataResultParsed';
import {
  ChartEmptyState,
  ChartOverflowState,
  ChartSeriesConfig,
  ChartTooltipContent,
  ChartWidgetFrame,
  CHART_DATA_POINT_COLOR_KEY,
  DEFAULT_CHART_COLORS,
  applyDataPointColors,
  getChartLegendProps,
  CHART_RESIZE_DEBOUNCE_MS,
  chartTooltipWrapperStyle,
  createChartSeries,
  getChartDataPointColor,
  PIE_MAX_RENDER_SLICES,
  useChartLegendVisibility,
} from './utils';
import { WidgetSettingsFactory } from '../../factories/WidgetSettings.factory';

const getPieCellColor = (
  series: ChartSeriesConfig[],
  item: ChartSeriesConfig,
  dataIndex: number
): string => {
  if (item.colorByDataPoint || series.length === 1) {
    return DEFAULT_CHART_COLORS[dataIndex % DEFAULT_CHART_COLORS.length];
  }

  return item.color;
};

const getDataPointColor = (entry: { [key: string]: string | number }, index: number): string => {
  const color = entry[CHART_DATA_POINT_COLOR_KEY];

  return typeof color === 'string' ? color : getChartDataPointColor(index);
};

const toResponsivePieRadius = (radius: number | undefined, fallback: number): string => {
  return `${radius ?? fallback}%`;
};

const getPieRadius = (
  item: ChartSeriesConfig,
  seriesCount: number,
  seriesIndex: number
): { innerRadius: number | string; outerRadius: number | string } => {
  if (seriesCount === 1) {
    return {
      innerRadius: toResponsivePieRadius(item.innerRadius, 56),
      outerRadius: toResponsivePieRadius(item.outerRadius, 86),
    };
  }

  const minRadius = 24;
  const maxRadius = 88;
  const ringGap = 2;
  const ringWidth = (maxRadius - minRadius - ringGap * (seriesCount - 1)) / seriesCount;
  const innerRadius = minRadius + seriesIndex * (ringWidth + ringGap);
  const outerRadius = innerRadius + ringWidth;

  return {
    innerRadius: `${innerRadius}%`,
    outerRadius: `${outerRadius}%`,
  };
};

// A pie with a single series legends its slices rather than its data streams,
// so those entries are identified by the row they were built from.
const getPieSliceLegendId = (dataKey: string, index: number): string => `${dataKey}-${index}`;

const createPieLegendPayload = (
  series: ChartSeriesConfig[],
  data: { [key: string]: string | number }[]
): { color: string; id: string; type: 'square'; value: string }[] => {
  if (series.length !== 1) {
    return series.map((item: ChartSeriesConfig) => ({
      color: item.color,
      id: item.dataKey,
      type: 'square' as const,
      value: item.name,
    }));
  }

  const [item] = series;

  return data.map((entry: { [key: string]: string | number }, index: number) => {
    const label = entry[item.nameKey ?? item.dataKey] ?? item.name;

    return {
      color: item.colorByDataPoint
        ? getDataPointColor(entry, index)
        : getPieCellColor(series, item, index),
      id: getPieSliceLegendId(item.dataKey, index),
      type: 'square' as const,
      value: String(label),
    };
  });
};

interface PieChartContentProps {
  mergedData: { [key: string]: string | number }[];
  parsedData: DataResultParsed;
  series: ChartSeriesConfig[];
  widgetSettings: WidgetSettingsDto;
}

/**
 * Guards the chart behind the slice-count check. The chart itself lives in
 * `PieChartBody` so that nothing it needs - including the legend payload, which
 * is one entry per slice - is built for a pie the user has not asked to draw.
 */
const PieChartContent = (props: PieChartContentProps): JSX.Element => {
  const [isRenderConfirmed, setIsRenderConfirmed] = useState<boolean>(false);
  // Every series draws its own ring over the same rows, so the sector count is
  // what the browser actually has to lay out - not the row count.
  const sliceCount = props.mergedData.length * props.series.length;

  if (sliceCount > PIE_MAX_RENDER_SLICES && !isRenderConfirmed) {
    return (
      <ChartOverflowState
        sliceCount={sliceCount}
        onRender={(): void => setIsRenderConfirmed(true)}
      />
    );
  }

  return <PieChartBody {...props} />;
};

const PieChartBody = ({
  mergedData,
  parsedData,
  series,
  widgetSettings,
}: PieChartContentProps): JSX.Element => {
  const legendPayload = createPieLegendPayload(series, mergedData);
  const { isSeriesHidden, toggleSeries } = useChartLegendVisibility(
    legendPayload.map((entry: { id: string }): string => entry.id)
  );
  const visualization = widgetSettings.type === 'pie-chart' ? widgetSettings.visualization : null;
  const showLabels = mergedData.length <= 10;
  // Rows carry the index they were built from so a hidden slice does not shift
  // the colours - or the legend ids - of the slices that stay.
  const rows = mergedData.map((entry: { [key: string]: string | number }, index: number) => ({
    entry,
    index,
  }));

  return (
    <ChartWidgetFrame>
      <div className="min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%" debounce={CHART_RESIZE_DEBOUNCE_MS}>
          <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
            {series.map((item: ChartSeriesConfig, seriesIndex: number) => {
              const { innerRadius, outerRadius } = getPieRadius(item, series.length, seriesIndex);
              const visibleRows =
                series.length === 1
                  ? rows.filter(
                      ({ index }: { index: number }): boolean =>
                        !isSeriesHidden(getPieSliceLegendId(item.dataKey, index))
                    )
                  : rows;

              return (
                <Pie
                  key={item.dataKey}
                  data={visibleRows.map(
                    ({ entry }: { entry: { [key: string]: string | number } }) => entry
                  )}
                  hide={isSeriesHidden(item.dataKey)}
                  dataKey={item.dataKey}
                  cx="50%"
                  cy="50%"
                  name={item.name}
                  nameKey={item.nameKey}
                  innerRadius={visualization?.shape === 'donut' ? innerRadius : 0}
                  outerRadius={outerRadius}
                  paddingAngle={mergedData.length > 24 ? 0 : 1}
                  stroke="#ffffff"
                  strokeWidth={1}
                  label={series.length === 1 && showLabels}
                  labelLine={series.length === 1 && showLabels}
                  isAnimationActive={false}
                >
                  {visibleRows.map(
                    ({
                      entry,
                      index,
                    }: {
                      entry: { [key: string]: string | number };
                      index: number;
                    }) => (
                      <Cell
                        key={`${item.dataKey}-${entry[item.nameKey ?? item.dataKey] ?? index}`}
                        fill={
                          item.colorByDataPoint
                            ? getDataPointColor(entry, index)
                            : getPieCellColor(series, item, index)
                        }
                      />
                    )
                  )}
                </Pie>
              );
            })}
            <Tooltip
              wrapperStyle={chartTooltipWrapperStyle}
              content={
                <ChartTooltipContent
                  fallbackColors={DEFAULT_CHART_COLORS}
                  markerClassName="rounded-sm"
                  valueFormatter={
                    parsedData.tooltip?.formatter
                      ? (value: number | string, name: string): [string, string] =>
                          parsedData.tooltip!.formatter!(value, name, parsedData.dataStreams)
                      : undefined
                  }
                />
              }
            />
            {parsedData.legend && parsedData.legend.show && visualization?.showLegend !== false && (
              <Legend
                iconType="square"
                payload={legendPayload.map((entry: { id: string }) => ({
                  ...entry,
                  inactive: isSeriesHidden(entry.id),
                }))}
                {...getChartLegendProps({
                  aside: visualization?.renderLegendAside === true,
                  markerClassName: 'rounded-sm',
                  onToggleSeries: legendPayload.length > 1 ? toggleSeries : undefined,
                })}
              />
            )}
          </PieChart>
        </ResponsiveContainer>
      </div>
    </ChartWidgetFrame>
  );
};

export class PieChartWidget implements Widget {
  public render(data: DataResult, widgetSettings: WidgetSettingsDto): ReactNode {
    const normalizedSettings = WidgetSettingsFactory.normalize(widgetSettings);
    const dataParser = new DataResultParser(widgetSettings.query.type);
    const parsedData = dataParser.parse(data, normalizedSettings.type);
    const noData = parsedData.dataStreams.every(
      (stream: DataResultParseStream) => stream.data.length === 0
    );

    if (noData) {
      return (
        <ChartEmptyState
          descriptionKey="dashboard_widget_no_slices"
          description="This widget has no slices to display."
        />
      );
    }

    const series = createChartSeries(parsedData.dataStreams, {
      getColor: ({ fill }: { fill?: string }): string | undefined => fill,
    });
    const mergedData = applyDataPointColors(
      dataParser.merge(parsedData),
      series.some((item: ChartSeriesConfig): boolean => !!item.colorByDataPoint)
    );

    return (
      <PieChartContent
        mergedData={mergedData}
        parsedData={parsedData}
        series={series}
        widgetSettings={normalizedSettings}
      />
    );
  }
}
